import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calculator, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
  }, [location]);

  const practiceAreas = [
    { name: 'Car Crashes', path: '/car-crashes' },
    { name: 'Motorcycle Crashes', path: '/motorcycle-crashes' },
    { name: 'Slip and Falls', path: '/slip-and-falls' },
    { name: 'First Responders', path: '/first-responder-crashes' },
    { name: 'Wrongful Death', path: '/wrongful-death' },
    { name: 'Maritime Injuries', path: '/maritime-injuries' },
  ];

  const topNavLinks = [
    { name: 'ABOUT', path: '/about-amy' },
    { name: 'SERVICES', path: null, hasDropdown: true },
    { name: 'AREAS', path: '/areas-we-serve' },
    { name: 'LEARNING CENTER', path: '/blog' },
    { name: 'FAQS', path: '/faqs' },
    { name: 'CONTACT', path: '/contact' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${isScrolled ? 'shadow-md' : ''}`}>
      {/* Top Navigation Bar */}
      <nav 
        data-testid="main-navbar"
        className="bg-white"
      >
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_fbb64d56-36dd-410e-80a1-5a26f6e86efa/artifacts/tlkgxq02_Amy%20C%20Brown%20no%20tag%20line%20blue%20copy.png"
                alt="Amy C Brown Law"
                className={`w-auto transition-all duration-300 ${isScrolled ? 'h-12 md:h-28' : 'h-24 md:h-28'}`}
              />
            </Link>

            {/* Desktop Top Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {topNavLinks.map((link) => (
                link.hasDropdown ? (
                  <div 
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button 
                      className="flex items-center gap-1 px-4 py-2 text-xs font-semibold tracking-widest text-slate-600 hover:text-primary transition-colors"
                      data-testid="services-dropdown"
                    >
                      {link.name}
                      <ChevronDown className={`w-3 h-3 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {servicesOpen && (
                      <div className="absolute top-full left-0 pt-2 z-50">
                        <div className="bg-white rounded-lg shadow-xl border border-slate-100 py-2 min-w-[260px]">
                          {practiceAreas.map((area) => (
                            <Link
                              key={area.path}
                              to={area.path}
                              className="block px-5 py-2.5 text-sm text-slate-600 hover:bg-primary/5 hover:text-primary transition-colors"
                            >
                              {area.name}
                            </Link>
                          ))}
                          {/* Divider + Resources */}
                          <div className="border-t border-slate-100 mt-1 pt-1">
                            <p className="px-5 pt-2 pb-1 text-[10px] font-semibold tracking-widest text-slate-400 uppercase">Resources</p>
                            <Link
                              to="/how-insurance-companies-handle-claims"
                              data-testid="nav-insurance-claims-link"
                              className="block px-5 py-2.5 text-sm text-slate-600 hover:bg-primary/5 hover:text-primary transition-colors"
                            >
                              How Insurance Companies Handle Claims
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`px-4 py-2 text-xs font-semibold tracking-widest transition-colors ${
                      location.pathname === link.path 
                        ? 'text-primary' 
                        : 'text-slate-600 hover:text-primary'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Button */}
            <a 
              href="tel:425-675-1224"
              data-testid="nav-phone-cta"
              className="hidden lg:flex items-center gap-2 bg-[#4A9B9B] text-white px-6 py-3 font-semibold text-sm tracking-wide hover:bg-[#3d8585] transition-all"
            >
              <Phone className="w-4 h-4" />
              (425) 675-1224
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-slate-700"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Secondary Bar - Practice Areas */}
        <div className="hidden lg:block border-t border-slate-300 py-1.5">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex items-center justify-between">
              {/* Practice Area Links */}
              <div className="flex items-center gap-6">
                {practiceAreas.map((area, index) => (
                  <React.Fragment key={area.path}>
                    <Link
                      to={area.path}
                      className={`text-sm font-medium transition-colors ${
                        location.pathname === area.path
                          ? 'text-primary'
                          : 'text-slate-700 hover:text-primary'
                      }`}
                    >
                      {area.name}
                    </Link>
                    {index < practiceAreas.length - 1 && (
                      <span className="text-slate-300">•</span>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Case Calculator Button */}
              <Link 
                to="/compensation-estimator"
                data-testid="nav-case-calculator"
                className="flex items-center gap-2 border-2 border-primary text-primary px-5 py-2 font-semibold text-sm hover:bg-primary hover:text-white transition-all"
              >
                <Calculator className="w-4 h-4" />
                Case Calculator
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-xl" data-testid="mobile-menu">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {/* Main Links */}
            <div className="space-y-1">
              <Link to="/" className="block py-2 text-sm font-semibold text-slate-700">HOME</Link>
              {topNavLinks.filter(l => !l.hasDropdown).map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`block py-2 text-sm font-semibold ${
                    location.pathname === link.path ? 'text-primary' : 'text-slate-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Practice Areas */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-400 tracking-wider mb-2">PRACTICE AREAS</p>
              {practiceAreas.map((area) => (
                <Link
                  key={area.path}
                  to={area.path}
                  className={`block py-2 pl-2 text-sm ${
                    location.pathname === area.path ? 'text-primary' : 'text-slate-600'
                  }`}
                >
                  {area.name}
                </Link>
              ))}
              <Link
                to="/how-insurance-companies-handle-claims"
                data-testid="mobile-nav-insurance-claims-link"
                className={`block py-2 pl-2 mt-2 text-sm border-t border-slate-100 pt-3 ${
                  location.pathname === '/how-insurance-companies-handle-claims' ? 'text-primary' : 'text-slate-600'
                }`}
              >
                How Insurance Companies Handle Claims
              </Link>
            </div>

            {/* CTAs */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <Link 
                to="/compensation-estimator"
                className="flex items-center justify-center gap-2 border-2 border-primary text-primary px-6 py-3 font-semibold w-full"
              >
                <Calculator className="w-4 h-4" />
                Case Calculator
              </Link>
              <a 
                href="tel:425-675-1224"
                className="flex items-center justify-center gap-2 bg-secondary text-white px-6 py-3 font-semibold w-full"
              >
                <Phone className="w-4 h-4" />
                Call (425) 675-1224
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
